import styled from '@emotion/styled'
import { IconButton } from '@mui/material'
import { ArrowDown, ArrowUp, Trash2, Eye } from 'lucide-react'
import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Label from '../form/Label'
import RichTextEditor from '../common/RichTextEditor'
import { TrainingSessionStep as TrainingSessionStepType } from '../../types/training-session.type'
import { Exercise } from '../../types/exercise.type'
import ExerciseDetailsModal from './ExerciseDetailsModal'

interface TrainingSessionStepProps {
    step: TrainingSessionStepType
    index: number
    totalSteps: number
    exercises: Exercise[]
    onStepChange: <K extends keyof TrainingSessionStepType>(
        index: number,
        field: K,
        value: string | number | undefined
    ) => void
    onMoveStep: (index: number, direction: 'up' | 'down') => void
    onRemoveStep: (index: number) => void
}

const TrainingSessionStep: React.FC<TrainingSessionStepProps> = ({
    step,
    index,
    totalSteps,
    exercises,
    onStepChange,
    onMoveStep,
    onRemoveStep,
}) => {
    const { t } = useTranslation()
    const [showExerciseModal, setShowExerciseModal] = useState(false)
    const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null)

    const handleInputChange = (field: keyof TrainingSessionStepType) => 
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
            onStepChange(index, field, e.target.value)
        }

    const handleDescriptionChange = (value: string) => {
        onStepChange(index, 'description', value)
    }

    const handleViewExercise = () => {
        if (step.exerciseId) {
            const exercise = exercises.find((ex) => ex.id === Number(step.exerciseId))
            if (exercise) {
                setSelectedExercise(exercise)
                setShowExerciseModal(true)
            }
        }
    }

    return (
        <StepCard className="step-card mb-3 p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    {t('coach.trainingSessions.stepNumber', { number: index + 1 })}
                </h6>
                <div className="d-flex gap-1">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation()
                            onMoveStep(index, 'up')
                        }}
                        disabled={index === 0}
                        title={t('coach.trainingSessions.moveUp')}
                    >
                        <ArrowUp size={16} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation()
                            onMoveStep(index, 'down')
                        }}
                        disabled={index === totalSteps - 1}
                        title={t('coach.trainingSessions.moveDown')}
                    >
                        <ArrowDown size={16} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation()
                            onRemoveStep(index)
                        }}
                        disabled={totalSteps <= 1}
                        title={t('coach.trainingSessions.deleteStep')}
                    >
                        <Trash2 size={16} />
                    </IconButton>
                </div>
            </div>

            <Form.Group className="mb-3">
                <Label>{t('coach.trainingSessions.stepTitle')}</Label>
                <Form.Control
                    type="text"
                    value={step.title}
                    onChange={handleInputChange('title')}
                    required
                />
            </Form.Group>

            <div className="row">
                <div className="col-md-6">
                    <Form.Group className="mb-3">
                        <Label>{t('coach.trainingSessions.stepStartTime')}</Label>
                        <Form.Control
                            type="time"
                            value={step.startTime}
                            onChange={handleInputChange('startTime')}
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-6">
                    <Form.Group className="mb-3">
                        <Label>{t('coach.trainingSessions.stepEndTime')}</Label>
                        <Form.Control
                            type="time"
                            value={step.endTime}
                            onChange={handleInputChange('endTime')}
                            min={step.startTime}
                            required
                        />
                    </Form.Group>
                </div>
            </div>

            <Form.Group className="mb-3">
                <Label>{t('coach.trainingSessions.stepExercise')}</Label>
                <ExerciseSelectContainer>
                    <Form.Select
                        value={step.exerciseId ?? ''}
                        onChange={handleInputChange('exerciseId')}
                    >
                        <option value="">{t('coach.trainingSessions.noExercise')}</option>
                        {exercises.map((exercise) => (
                            <option key={exercise.id} value={exercise.id}>
                                {exercise.title}
                            </option>
                        ))}
                    </Form.Select>
                    <ExerciseButtonsContainer>
                        {step.exerciseId && (
                            <IconButton
                                size="small"
                                onClick={handleViewExercise}
                                title={t('coach.trainingSessions.viewExercise')}
                                color="primary"
                            >
                                <Eye size={18} />
                            </IconButton>
                        )}
                    </ExerciseButtonsContainer>
                </ExerciseSelectContainer>
            </Form.Group>

            <Form.Group>
                <Label>{t('coach.trainingSessions.stepDescription')}</Label>
                <RichTextEditor
                    value={step.description || ''}
                    onChange={handleDescriptionChange}
                />
            </Form.Group>

            <ExerciseDetailsModal
                show={showExerciseModal}
                onClose={() => setShowExerciseModal(false)}
                exercise={selectedExercise}
            />
        </StepCard>
    )
}

const StepCard = styled.div`
    background-color: #f8f9fa;
    transition: all 0.2s;

    &:hover {
        background-color: #f1f3f5;
    }
`

const ExerciseSelectContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    flex-wrap: wrap;

    select {
        flex: 1;
        min-width: 200px;
    }
`

const ExerciseButtonsContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;

    button {
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        border-color: var(--primary-color, #b40000);
        color: var(--primary-color, #b40000);

        &:hover {
            background-color: var(--primary-color, #b40000);
            color: white;
        }
    }
`

export default TrainingSessionStep
